import "./styles.scss";

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";

import { useTasks } from "context/webmaster/tasks";
import { useTimeTracking } from "context/time-tracking";

import Table from "components/Table";
import PillGridNew from "components/PillGridNew";
import TaskInfo from "components/TaskInfo";

import task_status_data from "libs/task_status_data.json";
import task_priority_data from "libs/task_priority_data.json";

const mainClass = "tasks-webmaster-table";

const TasksTable = ({ setWebsiteInfoId, setExplanation }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, loading, filters, filterHandler, updateTask, refetchTasks } =
    useTasks();
  const { current, timeTracking } = useTimeTracking();

  const table_data = {
    keys: [
      {
        key: "created_at",
        value: t("Creation date"),
      },
      {
        key: "website",
        value: t("Website [SHORT]"),
      },
      {
        sort_key: "title",
        key: "title-full",
        value: t("Title"),
      },
      {
        key: "status",
        value: t("Status"),
      },
      {
        key: "priority",
        value: t("Priority"),
      },
      {
        key: "due_date",
        value: t("Due date [ALT]"),
      },
      {
        sort_key: "total_time",
        key: "time_tracked",
        value: t("Work time"),
      },
      {
        key: "details",
        sort_key: false,
        value: t("Details"),
      },
    ],
    items: data?.map(
      ({
        _id,
        created_at,
        priority,
        title,
        website,
        total_time,
        due_date,
        status,
      }) => {
        const status_data = task_status_data?.find(
          (item) => item?.value === status,
        );
        const priority_data = task_priority_data?.find(
          (item) => item?.value === priority,
        );

        return [
          {
            key: "created_at",
            type: "text",
            value: created_at ? moment(created_at).format("LL") : "-",
          },
          {
            key: "website",
            type: "clickable_text",
            value: {
              label: website?.name,
              icon: false,
              onclick: () => setWebsiteInfoId(website?._id),
            },
          },
          {
            key: "title-full",
            type: "link",
            value: {
              label: title,
              href: !!filters?.time_over
                ? `/tasks/${_id}/time-over`
                : `/tasks/${_id}`,
            },
          },
          {
            key: "status",
            type: "pill",
            value: {
              data: {
                value: status_data?.value,
                label: t(status_data?.label),
                color: status_data?.color,
              },
              options: task_status_data
                .filter((item) => item?.key !== "canceled")
                .map((item) => ({
                  ...item,
                  label: t(item.label),
                })),
              optionsFooter: (
                <TaskInfo onClick={() => setExplanation("status")} />
              ),
              isCompact: true,
              onChange: (option) => updateTask(_id, { status: option.value }),
            },
          },
          {
            key: "priority",
            type: "pill",
            value: {
              data: {
                value: priority_data?.value,
                label: t(priority_data?.label),
                color: priority_data?.color,
              },
              icon: "flag",
              options: task_priority_data.map((item) => ({
                ...item,
                label: t(item.label),
              })),
              optionsFooter: (
                <TaskInfo
                  type="priority"
                  onClick={() => setExplanation("priority")}
                />
              ),
              isCompact: true,
              onChange: (option) => updateTask(_id, { priority: option.value }),
            },
          },
          {
            key: "due_date",
            type: "due_date",
            value: {
              due_date: due_date,
              onChange: (date) => {
                updateTask(_id, { due_date: date });
              },
            },
          },
          {
            key: "time_tracked",
            type: "time_tracker",
            value: {
              label: total_time,
              onclick: () => timeTracking(_id, refetchTasks),
              start_time_tracked:
                current?.tasks?.[0]?._id === _id ? current?.start : null,
            },
          },
          {
            key: "details",
            type: "clickable_text",
            value: {
              label: t("View"),
              onclick: () => navigate(`/tasks/${_id}`),
            },
          },
        ];
      },
    ),
  };

  return (
    <div className={mainClass}>
      <Table
        data={table_data}
        loading={!!loading}
        sort={filters?.sort}
        setSort={(value) => filterHandler("sort", value)}
        filters_new={
          <div className={`${mainClass}__filters`}>
            <div>
              <PillGridNew
                header="Status"
                label={t("Select status")}
                selected={filters?.status}
                options={task_status_data
                  .filter((item) => item?.key !== "canceled")
                  .map((item) => ({
                    ...item,
                    label: t(item.label),
                  }))}
                optionsFooter={
                  <TaskInfo onClick={() => setExplanation("status")} />
                }
                onChange={(option) => filterHandler("status", option.key, true)}
              />
            </div>
            <div>
              <PillGridNew
                header={t("Priority")}
                label={t("Select priority")}
                selected={filters?.priority}
                icon="flag"
                options={task_priority_data.map((item) => ({
                  ...item,
                  label: t(item.label),
                }))}
                optionsFooter={
                  <TaskInfo
                    type="priority"
                    onClick={() => setExplanation("priority")}
                  />
                }
                onChange={(option) =>
                  filterHandler("priority", option.key, true)
                }
              />
            </div>
          </div>
        }
      />
    </div>
  );
};

export default TasksTable;
